import { useEffect, useState } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import AiImageButton from "@/components/AiImageButton";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";

export const EMPTY_BUSINESS = {
  reference: "", title: "", sector: "", region: "", city: "", profitability_rate: "",
  asking_price_range: "", short_description: "", hero_image: "", lat: 46.6, lng: 2.4,
  status: "available", full_description: "", video_url: "", revenue: "", ebitda: "", net_result: "", real_estate_price: "",
  employees: "", year_founded: "", asking_price: "", gallery: "",
  advisor_name: "PROAC Transmission", advisor_email: "contact@proac-transmission.fr", advisor_phone: "06 61 16 10 38",
};

export function buildPayload(form) {
  return {
    ...form,
    profitability_rate: form.profitability_rate === "" || form.profitability_rate == null ? null : parseFloat(form.profitability_rate),
    net_result: form.net_result === "" || form.net_result == null ? null : parseFloat(form.net_result),
    real_estate_price: form.real_estate_price === "" || form.real_estate_price == null ? null : parseFloat(form.real_estate_price),
    lat: parseFloat(form.lat) || 0,
    lng: parseFloat(form.lng) || 0,
    revenue: form.revenue === "" || form.revenue == null ? null : parseFloat(form.revenue),
    ebitda: form.ebitda === "" || form.ebitda == null ? null : parseFloat(form.ebitda),
    asking_price: form.asking_price === "" || form.asking_price == null ? null : parseFloat(form.asking_price),
    employees: form.employees === "" || form.employees == null ? null : parseInt(form.employees),
    year_founded: form.year_founded === "" || form.year_founded == null ? null : parseInt(form.year_founded),
    highlights: typeof form.highlights === "string"
      ? form.highlights.split("\n").map((s) => s.trim()).filter(Boolean)
      : (form.highlights || []),
    gallery: typeof form.gallery === "string"
      ? form.gallery.split(",").map((s) => s.trim()).filter(Boolean)
      : (form.gallery || []),
  };
}

export default function BusinessFormDialog({ open, onOpenChange, editing, initial, onSubmit, showStatus = false }) {
  const [form, setForm] = useState(initial || EMPTY_BUSINESS);
  useEffect(() => { setForm(initial || EMPTY_BUSINESS); }, [initial, open]);

  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });
  const field = (k, label, type = "text") => (
    <div>
      <Label className="text-xs uppercase tracking-wider text-[#475569]">{label}</Label>
      <Input type={type} value={form[k] ?? ""} onChange={set(k)} className="rounded-none mt-1.5 border-gray-300" data-testid={`biz-field-${k}`} />
    </div>
  );

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="rounded-none max-w-3xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="font-display text-2xl text-[#1B2A4A]">{editing ? "Modifier" : "Ajouter"} une entreprise</DialogTitle>
          <DialogDescription className="text-[#475569]">Renseignez les informations de l'entreprise à céder.</DialogDescription>
        </DialogHeader>
        <div className="grid sm:grid-cols-2 gap-4 py-2">
          {field("reference", "Référence")}
          {field("title", "Titre")}
          {field("sector", "Secteur d'activité")}
          {field("region", "Région")}
          {field("city", "Ville")}
          {field("profitability_rate", "Taux de rentabilité (%)", "number")}
          {field("asking_price_range", "Fourchette de prix (texte)")}
          {field("lat", "Latitude", "number")}
          {field("lng", "Longitude", "number")}
          <div className="sm:col-span-2"><Label className="text-xs uppercase tracking-wider text-[#475569]">Image principale (URL)</Label><Input value={form.hero_image} onChange={set("hero_image")} className="rounded-none mt-1.5 border-gray-300" data-testid="biz-field-hero_image" />
            <div className="mt-2 flex items-center gap-3">
              {form.hero_image && <img src={form.hero_image} alt="" data-testid="biz-hero-preview" className="w-24 h-16 object-cover border border-gray-200" />}
              <AiImageButton testid="biz-hero-ai" title={form.title} sector={form.sector} description={form.short_description} onGenerated={(url) => setForm((f) => ({ ...f, hero_image: url }))} />
            </div>
          </div>
          <div className="sm:col-span-2"><Label className="text-xs uppercase tracking-wider text-[#475569]">Description courte (teaser public)</Label><Textarea data-testid="biz-field-short_description" value={form.short_description} onChange={set("short_description")} className="rounded-none mt-1.5 border-gray-300" /></div>
          <div className="sm:col-span-2"><Label className="text-xs uppercase tracking-wider text-[#475569]">Points forts (un par ligne, publics)</Label><Textarea data-testid="biz-field-highlights" value={typeof form.highlights === "string" ? form.highlights : (form.highlights || []).join("\n")} onChange={set("highlights")} className="rounded-none mt-1.5 border-gray-300" /></div>
          <div className="sm:col-span-2 border-t border-gray-200 pt-3"><p className="text-xs uppercase tracking-widest text-[#E0C173]">Contenu réservé aux acheteurs</p></div>
          <div className="sm:col-span-2"><Label className="text-xs uppercase tracking-wider text-[#475569]">URL vidéo (YouTube/Vimeo)</Label><Input value={form.video_url} onChange={set("video_url")} className="rounded-none mt-1.5 border-gray-300" data-testid="biz-field-video_url" /></div>
          {field("revenue", "Chiffre d'affaires (€)", "number")}
          {field("ebitda", "EBE / EBITDA (€)", "number")}
          {field("net_result", "Résultat net (€)", "number")}
          {field("real_estate_price", "Foncier en option (€)", "number")}
          {field("asking_price", "Prix de cession (€)", "number")}
          {field("employees", "Effectif", "number")}
          {field("year_founded", "Année de création", "number")}
          {showStatus && (
            <div>
              <Label className="text-xs uppercase tracking-wider text-[#475569]">Statut</Label>
              <select value={form.status} onChange={set("status")} className="w-full mt-1.5 border border-gray-300 px-3 py-2 text-sm bg-white" data-testid="biz-field-status">
                <option value="available">Disponible (publiée)</option>
                <option value="pending">En attente de validation</option>
                <option value="sold">Cédée</option>
              </select>
            </div>
          )}
          <div className="sm:col-span-2"><Label className="text-xs uppercase tracking-wider text-[#475569]">Présentation détaillée</Label><Textarea value={form.full_description} onChange={set("full_description")} className="rounded-none mt-1.5 border-gray-300 min-h-[100px]" /></div>
          <div className="sm:col-span-2"><Label className="text-xs uppercase tracking-wider text-[#475569]">Galerie (URLs séparées par des virgules)</Label><Input value={typeof form.gallery === "string" ? form.gallery : (form.gallery || []).join(", ")} onChange={set("gallery")} className="rounded-none mt-1.5 border-gray-300" /></div>
          {field("advisor_name", "Conseiller — nom")}
          {field("advisor_email", "Conseiller — email")}
          {field("advisor_phone", "Conseiller — téléphone")}
        </div>
        <DialogFooter>
          <Button variant="outline" className="rounded-none" onClick={() => onOpenChange(false)}>Annuler</Button>
          <Button data-testid="biz-save-btn" onClick={() => onSubmit(buildPayload(form))} className="rounded-none bg-[#1B2A4A] hover:bg-[#0D1629]">Enregistrer</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
